import { FunctionComponent, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Checkbox, FormControlLabel, Radio, RadioGroup, TextField, Grid, Paper, IconButton, MenuItem, Select as MuiSelect, FormControl, InputLabel, Stack, Tooltip, Card, CardContent, Button, Divider, useMediaQuery } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useFormContext } from "react-hook-form";
import OnlineExamTamplete from "../PageTamplete/OnlineExampageTamplete/OnlineExampageTamplete";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import { listQuestionMapping } from "../../Redux/Slices/Questions/QuestionMappingSlice";
import { IQuestionMappingPayload } from "./IQuestions";
import { listExam } from "../../Redux/Slices/Exam/ListExamSlice";
import { listPracticeExam } from "../../Redux/Slices/PracticeExam/ListPracticeExamSlice";
import { listYearlevel_map } from "../../Redux/Slices/Yearlevel_map/ListYearlevel_mapSlice";
import { listQuestionSubType } from "../../Redux/Slices/QuestionSubType/ListQuestionSubTypeSlice";
import Table from "../../Components/Table/Table";
import { Columns } from "../../Components/Table/ITable";
import TextInputFieldC from "../../Components/TextInput/TextInputFieldC";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import NumberInputFieldlist from "../../Components/NumberInputlist/NumberInputFieldlist";
import { GridRenderCellParams } from "@mui/x-data-grid";
import ResponseDialog from "../../Components/ResponseDialog/ResponseDialog";
import { IAPIResponse } from "../../Redux/interfaces/IAPIResponse";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { UcatLayout } from "../../.MODULS/UCAT_EXAM_MODUL/Components/Shared";

interface PredefinedMappingLayoutProps {
    selectedIds: number[];
    onSelectionChange: (ids: number[]) => void;
    stagedIds?: number[];
    onAdd?: () => void;
    displayDataList?: any[];
    displayDataListsub?: any[];
    onDeleteMain?: (index: number) => void;
    onDeleteSub?: (index: number) => void;
    onEditMain?: (index: number) => void;
    onEditSub?: (index: number) => void;
    excludeId?: number | null;
    excludeType: 'exam' | 'test';
    statusOptions?: AutocompleteOption[];
    isEditing?: boolean;
}

// Utility function for tick and cross marks
const renderTickCross = (value: boolean) => {
    return (
        <span className={value ? "tick-mark" : "cross-mark"}>
            {value ? "✓" : "✗"}
        </span>
    );
};

const PredefinedMappingLayout: FunctionComponent<PredefinedMappingLayoutProps> = ({
    selectedIds,
    onSelectionChange,
    onAdd,
    displayDataList = [],
    displayDataListsub = [],
    onDeleteMain,
    onDeleteSub,
    onEditMain,
    onEditSub,
    excludeId,
    excludeType,
    stagedIds = [],
    statusOptions = [],
    isEditing = false
}) => {
    const { watch, setValue, getValues, formState: { errors } } = useFormContext();
    const dispatch = useAppDispatch();
    const isSmallScreen = useMediaQuery('(max-width:950px)');
    const isProcessing = useRef(false);

    const [searchText, setSearchText] = useState("");
    const [allQuestionsMap, setAllQuestionsMap] = useState<Record<number, any>>({});
    const [unselectedQuestionIds, setUnselectedQuestionIds] = useState<number[]>([]);
    const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set());
    const [loadingPages, setLoadingPages] = useState<Set<number>>(new Set());
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [freeTier, setFreeTier] = useState<boolean | null>(null);
    const [yearGroupId, setYearGroupId] = useState<number | null>(null);
    const [excludeExamId, setExcludeExamId] = useState<number | null>(null);
    const [excludeTestId, setExcludeTestId] = useState<number | null>(null);
    const [previewData, setPreviewData] = useState<any>(null);
    const [alertResponse, setAlertResponse] = useState<IAPIResponse<any>>({} as IAPIResponse<any>);

    const { data: questionMappingData, isLoading } = useAppSelector((state) => state.QuestionMappingSlice);
    const { data: exams } = useAppSelector((state) => state.ListExamSlice);
    const { data: tests } = useAppSelector((state) => state.ListPracticeExamSlice);
    const { data: yearGroups } = useAppSelector((state) => state.ListYearlevel_mapSlice);
    const { data: listQuestions } = useAppSelector((state) => state.ListQuestionTypeSlice);
    const { data: listQuestionssub } = useAppSelector((state) => state.ListQuestionSubTypeSlice);

    useEffect(() => {
        dispatch(listExam());
        dispatch(listPracticeExam());
        dispatch(listYearlevel_map());
    }, [dispatch]);

    // Determine correct field names based on which form we are in
    const isPracticeExam = excludeType === 'test';
    const fieldNames = {
        name: isPracticeExam ? "name" : "model_name",
        questionCount: isPracticeExam ? "question_count" : "question_counts",
        duration: isPracticeExam ? "duration" : "durations",
        questionTypeId: isPracticeExam ? "question_type_id" : "questionTypes",
        subtypeId: isPracticeExam ? "subtype_id" : "subtypes",
        subtypeCountField: isPracticeExam ? "sub_question_count" : "subtype_counts",
    };

    const watchedQuestionTypeId = watch(fieldNames.questionTypeId);
    const watchedSubtypeId = watch(fieldNames.subtypeId);
    const questionCount = watch(fieldNames.questionCount) || 0;
    const subtypeCount = watch(fieldNames.subtypeCountField) || 0;
    const targetCount = watchedSubtypeId ? subtypeCount : questionCount;

    const [previousQuestionTypeId, setPreviousQuestionTypeId] = useState<number | null>(null);

    useEffect(() => {
        if (watchedQuestionTypeId) {
            console.log("excludeId", excludeId);
            const payload: IQuestionMappingPayload = {
                question_type_id: watchedQuestionTypeId,
                subtype_id: watchedSubtypeId || null,
                search_text: searchText || null,
                free_tier: freeTier,
                yeargroup_country_map_id: yearGroupId,
                exclude_exam_model_id: excludeExamId,
                exclude_test_id: excludeTestId,
                page,
                prioritize_exam_model_id: excludeId ? excludeId : null,
                page_size: pageSize
            };
            dispatch(listQuestionMapping(payload));
            dispatch(listQuestionSubType({ questiontype_id: watchedQuestionTypeId }));

            // Clear dependent fields ONLY when question type changes, not when subtype changes
            if (previousQuestionTypeId !== null && previousQuestionTypeId !== watchedQuestionTypeId) {
                setValue(fieldNames.questionCount, null);
                setValue(fieldNames.duration, null);
                setValue("sequence", null);
            }

            setPreviousQuestionTypeId(watchedQuestionTypeId);
        }
    }, [dispatch, watchedQuestionTypeId, watchedSubtypeId, searchText, freeTier, yearGroupId, excludeExamId, excludeTestId, page, pageSize, setValue, fieldNames.questionCount, fieldNames.duration, previousQuestionTypeId]);

    // Clear cache when filters change
    useEffect(() => {
        setAllQuestionsMap({});
        setUnselectedQuestionIds([]);
        setLoadedPages(new Set());
        setLoadingPages(new Set());
        setPage(1); // Reset to page 1 when filters change
    }, [watchedQuestionTypeId, watchedSubtypeId, searchText, freeTier, yearGroupId, excludeExamId, excludeTestId]);

    useEffect(() => {
        const questions = questionMappingData?.data?.questions;
        if (Array.isArray(questions) && questions.length > 0) {
            // Update allQuestionsMap
            setAllQuestionsMap(prev => {
                const newMap = { ...prev };
                questions.forEach((q: any) => {
                    newMap[q.id] = q;
                });
                return newMap;
            });

            // Track unselected question IDs in server order
            setUnselectedQuestionIds(prev => {
                const existingIds = new Set(prev);
                const newIds = questions
                    .map((q: any) => q.id)
                    .filter(id => !existingIds.has(id));
                return [...prev, ...newIds];
            });

            // Mark current page as loaded
            setLoadedPages(prev => new Set(prev).add(page));

            // Remove from loading pages
            setLoadingPages(prev => {
                const newSet = new Set(prev);
                newSet.delete(page);
                return newSet;
            });
        }
    }, [questionMappingData, page]);

    const QuestionList: AutocompleteOption[] = useMemo(() => {
        const data = listQuestions?.data;
        return Array.isArray(data) ? data.filter((r: any) => r.Status !== false).map((q: any) => ({ label: q.name, value: q.id })) : [];
    }, [listQuestions]);

    const QuestionsubList: AutocompleteOption[] = useMemo(() => {
        const data = listQuestionssub?.data;
        return Array.isArray(data) ? data.filter((r: any) => r.status !== false).map((s: any) => ({ label: s.subtype_name, value: s.id })) : [];
    }, [listQuestionssub]);

    const [editingIds, setEditingIds] = useState<number[]>([]);
    const [originalSequenceOrder, setOriginalSequenceOrder] = useState<number[]>([]);
    const prevIsEditing = useRef(isEditing);

    useEffect(() => {
        if (isEditing) {
            setEditingIds(selectedIds);
            // Store the original order when entering edit mode
            setOriginalSequenceOrder([...selectedIds]);
        } else {
            setEditingIds([]);
            setOriginalSequenceOrder([]);
        }
        prevIsEditing.current = isEditing;
    }, [isEditing, selectedIds]);

    const effectiveStagedIds = useMemo(() => {
        if (!isEditing) return stagedIds;
        return stagedIds.filter(sid => !editingIds.some(eid => eid != null && Number(eid) == Number(sid)));
    }, [stagedIds, editingIds, isEditing]);

    const stagedQuestionTypeIds = useMemo(() => {
        const mainIds = displayDataList.map(d => d?.questionTypes?.value ?? d?.questionTypes?.id);
        const subIds = displayDataListsub.map(d => d?.questionTypes?.value ?? d?.questionTypes?.id);
        return [...mainIds, ...subIds].filter(Boolean);
    }, [displayDataList, displayDataListsub]);

    // Ensure stagedQuestionTypeIds calculation is robust if needed, but above seems fine or user didn't mention it.
    // Wait, I should not change stagedQuestionTypeIds unless needed, but I am in a Replace block encompassing it.
    // I already have it in the original code. I will include it unchanged or with minor safety fix if needed.

    const filteredQuestionList = useMemo(() => {
        const currentId = getValues(fieldNames.questionTypeId);
        return QuestionList.filter(q =>
            !stagedQuestionTypeIds.includes(q.value) ||
            (isEditing && q.value === currentId)
        );
    }, [QuestionList, stagedQuestionTypeIds, isEditing, getValues, fieldNames.questionTypeId]);

    const examOptions: AutocompleteOption[] = useMemo(() => {
        const data = exams?.data;
        return Array.isArray(data) ? data.map((e: any) => ({ label: e.name || e.model_name, value: e.id })) : [];
    }, [exams]);

    const testOptions: AutocompleteOption[] = useMemo(() => {
        const data = tests?.data;
        return Array.isArray(data) ? data.map((t: any) => ({ label: t.name, value: t.id })) : [];
    }, [tests]);

    const yearGroupOptions: AutocompleteOption[] = useMemo(() => {
        const data = (yearGroups as any)?.data;
        return Array.isArray(data) ? data.map((y: any) => ({
            label: `${y.yeargroup?.name || ''} - ${y.region?.name || ''}`,
            value: y.id
        })) : [];
    }, [yearGroups]);

    const columns: Columns = [
        {
            field: "sequence",
            renderHeader: () => <b>Sequence</b>,
            width: 80,
            renderCell: (params: GridRenderCellParams) => {
                const rowId = params?.row?.id || params?.row?.question_id || params?.id;
                
                // Use original sequence order when editing, otherwise use current selectedIds order
                const sequenceArray = isEditing && originalSequenceOrder.length > 0 ? originalSequenceOrder : selectedIds;
                const sequenceIndex = sequenceArray.findIndex(sid => {
                    if (sid == null) return false;
                    const sidVal = (typeof sid === 'object') ? (sid as any).id || (sid as any).question_id : sid;
                    return String(sidVal).trim() === String(rowId).trim();
                });
                
                const isSelected = selectedIds.some(sid => {
                    if (sid == null) return false;
                    const sidVal = (typeof sid === 'object') ? (sid as any).id || (sid as any).question_id : sid;
                    return String(sidVal).trim() === String(rowId).trim();
                });
                
                const isStaged = effectiveStagedIds.some(sid => {
                    if (sid == null) return false;
                    const sidVal = (typeof sid === 'object') ? (sid as any).id || (sid as any).question_id : sid;
                    return String(sidVal).trim() === String(rowId).trim();
                }) && !isSelected;

                return (
                    <Box
                        onClick={() => !isStaged && handleSequenceClick(rowId)}
                        sx={{
                            width: 36,
                            height: 36,
                            minWidth: 36,
                            flexShrink: 0,
                            borderRadius: '50%',
                            backgroundColor: isSelected ? '#009DFF' : (isStaged ? '#FFF3E0' : '#F0F1F7'),
                            color: isSelected ? '#fff' : (isStaged ? '#E65100' : '#000'),
                            border: `2px solid ${isSelected ? '#009DFF' : (isStaged ? '#FFB74D' : '#D1D5DB')}`,
                            cursor: isStaged ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': !isStaged ? {
                                backgroundColor: isSelected ? '#007acc' : '#e0e0e0',
                                transform: 'scale(1.1)',
                                borderColor: isSelected ? '#007acc' : '#bdbdbd'
                            } : {}
                        }}
                    >
                        <Typography
                            sx={{
                                display: 'grid',
                                placeContent: 'center',
                                width: '100%',
                                height: '100%',
                                fontWeight: '600',
                                fontSize: '14px',
                                lineHeight: 'normal' // Reset line-height as placeContent handles vertical alignment
                            }}
                        >
                            {isSelected && sequenceIndex !== -1 ? (sequenceIndex + 1) : ''}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: "question",
            renderHeader: () => <b>Question</b>,
            flex: 2,
            renderCell: (params: GridRenderCellParams) => (
                <Box sx={{ py: 1 }}>
                    <Typography variant="body2">{params.row.question}</Typography>
                </Box>
            )
        },
        {
            field: "Question_type_name",
            renderHeader: () => <b>Type</b>,
            flex: 1
        },
        {
            field: "subType_name",
            renderHeader: () => <b>Subtype</b>,
            flex: 1
        },
        {
            field: "preview",
            renderHeader: () => <b>Preview</b>,
            width: 100,
            renderCell: (params: GridRenderCellParams) => (
                <Tooltip title="Preview Question">
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handlePreview(params.row)}
                    >
                        <VisibilityIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )
        },
        {
            field: "question_url",
            renderHeader: () => <b>Image</b>,
            width: 120,
            renderCell: (params: GridRenderCellParams) => params.row.question_url ? (
                <img src={params.row.question_url} alt="question" style={{ width: '50px', height: 'auto' }} />
            ) : "-"
        },
        {
            field: "free_tier",
            renderHeader: () => <b>Free Tier</b>,
            width: 100,
            headerAlign: "center",
            align: "center",
            renderCell: (params: GridRenderCellParams) => renderTickCross(params.value),
        }
    ];

    // 6. Check if we have enough data
    const [missingPage, setMissingPage] = useState<number | null>(null);

    useEffect(() => {
        if (missingPage && !loadedPages.has(missingPage) && !loadingPages.has(missingPage)) {
            setLoadingPages(prev => new Set(prev).add(missingPage));

            const payload: IQuestionMappingPayload = {
                question_type_id: watchedQuestionTypeId,
                subtype_id: watchedSubtypeId || null,
                search_text: searchText || null,
                free_tier: freeTier,
                yeargroup_country_map_id: yearGroupId,
                exclude_exam_model_id: excludeExamId,
                exclude_test_id: excludeTestId,
                page: missingPage,
                prioritize_exam_model_id: excludeId ? excludeId : null,
                page_size: pageSize
            };

            dispatch(listQuestionMapping(payload));
        }
    }, [missingPage, watchedQuestionTypeId, watchedSubtypeId, searchText, freeTier, yearGroupId, excludeExamId, excludeTestId, excludeId, dispatch, loadedPages, loadingPages]);

    const sortedRows = useMemo(() => {
        const selectedIdSet = new Set(selectedIds);
        const startIndex = (page - 1) * pageSize;

        // Use original sequence order when editing, otherwise use current selectedIds order
        const orderedSelectedIds = isEditing && originalSequenceOrder.length > 0 ? originalSequenceOrder : selectedIds;

        // 1. Get selected questions for this page in the correct order
        const allSelectedQuestions = orderedSelectedIds
            .map(id => allQuestionsMap[id])
            .filter(Boolean);
        const pageSelectedRows = allSelectedQuestions.slice(startIndex, startIndex + pageSize);

        // 2. Calculate how many unselected questions we need
        const slotsRemaining = pageSize - pageSelectedRows.length;

        if (slotsRemaining <= 0) {
            // Page is completely filled with selected items
            return pageSelectedRows;
        }

        // 3. Calculate offset into unselectedQuestionIds array
        // We need to account for selected items that appeared on previous pages
        const selectedItemsOnPreviousPages = Math.min(startIndex, orderedSelectedIds.length);
        const unselectedOffset = startIndex - selectedItemsOnPreviousPages;

        // 4. Get unselected question IDs for this page
        const unselectedIdsForPage = unselectedQuestionIds
            .filter(id => !selectedIdSet.has(id))
            .slice(unselectedOffset, unselectedOffset + slotsRemaining);

        // 5. Map IDs to actual question objects from cache
        const pageUnselectedRows = unselectedIdsForPage
            .map(id => allQuestionsMap[id])
            .filter(Boolean);

        // 6. Check if we have enough data
        const missingCount = slotsRemaining - pageUnselectedRows.length;
        if (missingCount > 0 && unselectedQuestionIds.length > 0) {
            // We need more data - find which page we need to fetch
            const totalQuestionsNeeded = unselectedOffset + slotsRemaining;
            const pagesNeeded = Math.ceil(totalQuestionsNeeded / pageSize);

            for (let p = 1; p <= pagesNeeded; p++) {
                if (!loadedPages.has(p) && !loadingPages.has(p)) {
                    setMissingPage(p);
                    break;
                }
            }
        } else {
            setMissingPage(null);
        }

        return [...pageSelectedRows, ...pageUnselectedRows];
    }, [selectedIds, originalSequenceOrder, isEditing, allQuestionsMap, unselectedQuestionIds, page, pageSize, loadedPages, loadingPages]);

    const handlePreview = (row: any) => {
        // Map the data to the format expected by templates
        const isYear12 = row?.year_levels?.includes("Year 12");

        const previewDataObj = {
            test_name: "Question Preview",
            Question_type_name: row.Question_type_name,
            subtype_name: row.subType_name,
            prompt: row.prompt,
            prompt_url: row.prompt_url,
            prompt_imgWidth: row.promptImgWidth || 90,
            instruction: row.instruction,
            question: row.question,
            question_url: row.question_url,
            imgWidth: row.imgWidth || 90,
            year_levels: row.year_levels || [],
            options: row.options?.map((opt: any) => ({
                id: opt.id,
                option: opt.optionname, // Template looks for 'option'
                option_url: row.option_type === 'image' ? opt.optionname : null
            })) || [],
            explanation: row.explanation || null
        };

        setPreviewData(previewDataObj);
    };

    const subtypeSequence = watch("subtype_sequences");

    const handleToggleQuestion = (id: number) => {
        const isSelected = selectedIds.some(sid => String(sid) === String(id));
        const limit = subtypeCount || questionCount;

        if (!isSelected) {
            if (watchedSubtypeId) {
                if (!subtypeCount || subtypeCount <= 0) {
                    setAlertResponse({
                        status: false,
                        message: "Please set subtype count first.",
                        data: null,
                        status_code: 400
                    });
                    return;
                }
                if (!subtypeSequence || subtypeSequence <= 0) {
                    setAlertResponse({
                        status: false,
                        message: "Please set subtype sequence first.",
                        data: null,
                        status_code: 400
                    });
                    return;
                }
            } else if (!limit || limit <= 0) {
                setAlertResponse({
                    status: false,
                    message: "Please set question count first.",
                    data: null,
                    status_code: 400
                });
                return;
            }
            if (selectedIds.length >= limit) {
                setAlertResponse({
                    status: false,
                    message: `You can only select up to ${limit} questions.`,
                    data: null,
                    status_code: 400
                });
                return;
            }
        }

        if (isSelected) {
            onSelectionChange(selectedIds.filter(sid => sid != null && String(sid).trim() !== String(id).trim()));
        } else if (!effectiveStagedIds.some(sid => sid != null && String(sid).trim() === String(id).trim())) {
            onSelectionChange([...selectedIds, id]);
        }
    };

    // Add click handler for the sequence circle
    const handleSequenceClick = (id: number) => {
        if (!id || isProcessing.current) return;
        isProcessing.current = true;

        const isSelected = selectedIds.some(sid => sid != null && String(sid).trim() === String(id).trim());
        if (isSelected) {
            // If already selected, remove from selection
            onSelectionChange(selectedIds.filter(sid => sid != null && String(sid).trim() !== String(id).trim()));
        } else if (!effectiveStagedIds.some(sid => sid != null && String(sid).trim() === String(id).trim())) {
            // If not selected and not staged, add to selection
            handleToggleQuestion(id);
        }

        setTimeout(() => { isProcessing.current = false; }, 10);
    };

    if (previewData && previewData?.year_levels?.includes("Year 12")) {
        return (
            <UcatLayout
                headerTitle={previewData.test_name || "Question Preview"}
                previewData={previewData}
                onBack={() => setPreviewData(null)}
                showCalculatorButton={true}
                showExplainAnswerButton={true}
            />
        );
    }
    else if (previewData && !previewData?.year_levels?.includes("Year 12")) {
        return <OnlineExamTamplete previewData={previewData} onBack={() => setPreviewData(null)} />;
    }

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 4 }, bgcolor: '#f5f7f9', minHeight: '100vh', overflowX: 'hidden', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}>
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 2.5, md: 3 }, mb: 3, borderRadius: '12px' }}>


                <Grid container spacing={{ xs: 1, sm: 2 }}>
                    {/* Basic Configuration */}
                    <Grid item xs={12} sm={6}>
                        <TextInputFieldC name={fieldNames.name} label="Model Name" type="text" fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Select
                            name={fieldNames.questionTypeId}
                            label="Question Type"
                            options={filteredQuestionList}
                            value={QuestionList.find(o => o.value === watchedQuestionTypeId) || null}
                            onChange={(_, val) => setValue(fieldNames.questionTypeId, (val as AutocompleteOption)?.value || null)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <NumberInputFieldlist name={fieldNames.questionCount} label="Question Count" type="number" fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <NumberInputField name={fieldNames.duration} label="Duration (Min)" type="number" fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <NumberInputField name="sequence" label="Sequence" type="number" fullWidth />
                    </Grid>

                    {/* Primary Filters */}

                    <Grid item xs={12} sm={2}>
                        <Select
                            name="yeargroup"
                            label="Filter by Year Group"
                            options={yearGroupOptions}
                            value={yearGroupOptions.find(o => o.value === yearGroupId) || null}
                            onChange={(_, val) => setYearGroupId((val as AutocompleteOption)?.value || null)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={3}>
                        <Select
                            name="exclude_exam"
                            label="Exclude Existing Exam"
                            options={examOptions}
                            value={examOptions.find(o => o.value === excludeExamId) || null}
                            onChange={(_, val) => setExcludeExamId((val as AutocompleteOption)?.value || null)}
                            infoTooltip="Exclude questions from the selected Exam"
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <Select
                            name="exclude_test"
                            label="Exclude Existing Test"
                            options={testOptions}
                            value={testOptions.find(o => o.value === excludeTestId) || null}
                            onChange={(_, val) => setExcludeTestId((val as AutocompleteOption)?.value || null)}
                            infoTooltip="Exclude questions from the selected Test"
                        />
                    </Grid>
                    {/* Secondary Filters */}
                    <Grid item xs={12} sm={4}>
                        <Select
                            name="subtype_counts_filter"
                            label="Filter By Subtype"
                            options={QuestionsubList}
                            value={QuestionsubList.find(o => o.value === watchedSubtypeId) || null}
                            onChange={(_, val) => setValue(fieldNames.subtypeId, (val as AutocompleteOption)?.value || null)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <NumberInputFieldlist name="subtype_counts" label="Subtype Count" type="number" fullWidth />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <NumberInputField name="subtype_sequences" label="Subtype Sequence" type="number" fullWidth />
                    </Grid>

                    {/* Selection and Status */}
                    <Grid item xs={12} sm={4}>
                        <Box>
                            {isSmallScreen ? (
                                <Select
                                    name="question_selection_type"
                                    label="Selection Mode"
                                    options={[
                                        { label: "Random", value: "random" },
                                        { label: "Predefined", value: "predefined" }
                                    ]}
                                    value={[
                                        { label: "Random", value: "random" },
                                        { label: "Predefined", value: "predefined" }
                                    ].find(o => o.value === (getValues("question_selection_type") || "random"))}
                                    onChange={(_, val) => {
                                        setValue("question_selection_type", (val as AutocompleteOption)?.value || "random", { shouldValidate: true });
                                    }}
                                    fullWidth
                                    infoTooltip="Choose how questions are selected for this section. 'Random' selects questions randomly from the available pool, while 'Predefined' allows you to manually select specific questions."
                                />
                            ) : (
                                <>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="subtitle2" sx={{ mb: 0.5, color: '#666' }}>
                                            Selection Mode
                                        </Typography>
                                        <Tooltip
                                            title="Choose how questions are selected for this section. 'Random' selects questions randomly from the available pool, while 'Predefined' allows you to manually select specific questions."
                                            arrow
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        fontSize: '1rem',
                                                    }
                                                }
                                            }}
                                        >
                                            <IconButton size="small" tabIndex={-1} sx={{ p: 0, mb: 0.5 }}>
                                                <InfoOutlinedIcon sx={{ fontSize: '1.5rem', color: 'action.active' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                    <RadioGroup
                                        row
                                        value={getValues("question_selection_type")}
                                        onChange={(e) => {
                                            setValue("question_selection_type", e.target.value as 'random' | 'predefined', { shouldValidate: true });
                                        }}
                                    >
                                        <FormControlLabel
                                            value="random"
                                            control={<Radio size="small" />}
                                            label={<Typography variant="body2">Random</Typography>}
                                        />
                                        <FormControlLabel
                                            value="predefined"
                                            control={<Radio size="small" />}
                                            label={<Typography variant="body2">Predefined</Typography>}
                                        />
                                    </RadioGroup>
                                </>
                            )}
                        </Box>
                    </Grid>

                      {/* <Grid item xs={12} sm={4}>
                                  <Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ mb: 0.5, color: "#666" }}
                                      >
                                        Free Tier
                                      </Typography>
                                      <Tooltip
                                        title="Click Free Tier to show exam to free users. If 'No' is selected, only premium users can access this exam."
                                        arrow
                                        slotProps={{
                                          tooltip: {
                                            sx: {
                                              fontSize: "1rem",
                                            },
                                          },
                                        }}
                                      >
                                        <IconButton
                                          size="small"
                                          tabIndex={-1}
                                          sx={{ p: 0, mb: 0.5 }}
                                        >
                                          <InfoOutlinedIcon
                                            sx={{
                                              fontSize: "1.5rem",
                                              color: "action.active",
                                            }}
                                          />
                                        </IconButton>
                                      </Tooltip>
                                    </Stack>
                                    <RadioGroup
                                      row
                                      value={watch("free_tier") !== undefined ? (watch("free_tier") ? "true" : "false") : "false"}
                                      onChange={(e) => {
                                        const val = e.target.value === "true";
                                        setValue("free_tier", val, { shouldValidate: true });
                                        setFreeTier(val);
                                      }}
                                    >
                                      <FormControlLabel
                                        value="true"
                                        control={<Radio size="small" />}
                                        label={<Typography variant="body2">Yes</Typography>}
                                      />
                                      <FormControlLabel
                                        value="false"
                                        control={<Radio size="small" />}
                                        label={<Typography variant="body2">No</Typography>}
                                      />
                                    </RadioGroup>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "#009dff",
                                        fontSize: "10px",
                                        display: "block",
                                        mt: -0.5,
                                        ml: 0.5,
                                      }}
                                    >
                                      (Note: Click Free Tier to show exam to free users)
                                    </Typography>
                                  </Box>
                                </Grid> */}


                    <Grid item xs={12} sm={4}>
                        {statusOptions.length > 0 && (
                            <Select
                                name="status"
                                label="Status"
                                fullWidth
                                options={statusOptions}
                                value={
                                    statusOptions?.find(
                                        (role) => role.value === getValues("status")
                                    ) ?? null
                                }
                                onChange={(_, value) => {
                                    const val = value as AutocompleteOption;
                                    setValue("status", val?.value, { shouldValidate: true });
                                }}
                            />
                        )}
                        {errors.status && (
                            <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                                {String(errors.status?.message || "Required")}
                            </Typography>
                        )}
                    </Grid>

                   

                    {/* Search and Page Controls */}
                    <Grid item xs={12} sm={8}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search Question Content..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                endAdornment: (
                                    <IconButton onClick={() => setPage(1)}>
                                        <SearchIcon />
                                    </IconButton>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Items Per Page</InputLabel>
                            <MuiSelect
                                value={pageSize}
                                label="Items Per Page"
                                onChange={(e) => {
                                    setPageSize(e.target.value as number);
                                    setPage(1);
                                }}
                            >
                                <MenuItem value={10}>10 Items</MenuItem>
                                <MenuItem value={20}>20 Items</MenuItem>
                                <MenuItem value={50}>50 Items</MenuItem>
                            </MuiSelect>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Staging Area - Added Sets and Add Button */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, bgcolor: '#f0f7ff', p: 2, borderRadius: '12px', border: '1px solid #cce4ff' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#009DFF' }}>
                            Selection Staging
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: selectedIds.length > targetCount ? 'error.main' : 'text.primary' }}>
                            Current Selection: {selectedIds.length} / {targetCount}
                        </Typography>
                    </Box>

                    {onAdd && (
                        <Button
                            variant="contained"
                            startIcon={<AddCircleOutlineIcon />}
                            onClick={onAdd}
                            disabled={selectedIds.length === 0 || selectedIds.length !== Number(targetCount)}
                            sx={{
                                bgcolor: '#009DFF',
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                '&:hover': { bgcolor: '#007acc' }
                            }}
                        >
                            {isEditing ? "Update Model" : "Add to Model"}
                        </Button>
                    )}
                </Box>

                {/* Added Sets Cards */}
                {/* Added Sets Cards */}
                <Box>
                    {/* Question Types Section */}
                    {displayDataList.length > 0 && (
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            {displayDataList.map((data, index) => (
                                <Grid item xs={12} sm={6} md={4} key={`main-${index}`}>
                                    <Card sx={{ bgcolor: '#f5f5f5', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                        <CardContent sx={{ p: '12px !important', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{data?.questionTypes?.label}</Typography>
                                                <Typography variant="caption" color="textSecondary">{data?.question_counts} Questions • Predefined</Typography>
                                            </Box>
                                            <Box>
                                                <IconButton size="small" onClick={() => onEditMain?.(index)} sx={{ color: 'primary.main', mr: 1 }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => onDeleteMain?.(index)} sx={{ color: 'error.main' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {/* Divider */}
                    {displayDataList.length > 0 && displayDataListsub.length > 0 && (
                        <Divider sx={{ my: 3 }} />
                    )}

                    {/* Subtypes Section */}
                    {displayDataListsub.length > 0 && (
                        <Grid container spacing={2}>
                            {displayDataListsub.map((data, index) => (
                                <Grid item xs={12} sm={6} md={4} key={`sub-${index}`}>
                                    <Card sx={{ bgcolor: '#f5f5f5', borderRadius: '10px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                        <CardContent sx={{ p: '12px !important', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{data?.questionTypes?.label} - {data?.subtypes?.label || QuestionsubList.find(s => s.value === (data?.subtypes?.value || data?.subtypes))?.label}</Typography>
                                                <Typography variant="caption" color="textSecondary">{data?.subtype_counts || data?.sub_question_count} Questions • Predefined</Typography>
                                            </Box>
                                            <Box>
                                                <IconButton size="small" onClick={() => {
                                                    if (data?.question_mappings) {
                                                        onSelectionChange(data.question_mappings);
                                                    }
                                                    setValue(fieldNames.subtypeId, data?.subtypes?.value || data?.subtypes);
                                                    onEditSub?.(index);
                                                }} sx={{ color: 'primary.main', mr: 1 }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => onDeleteSub?.(index)} sx={{ color: 'error.main' }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}

                    {displayDataList.length === 0 && displayDataListsub.length === 0 && (
                        <Grid container>
                            <Grid item xs={12}>
                                <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}>
                                    No question sets added to the model yet.
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </Box>
            </Box>

            <Paper elevation={2} sx={{ p: 0, borderRadius: '12px', width: '100%', maxWidth: '100%', minWidth: 0 }}>
                <Table
                    rows={sortedRows}
                    columns={columns}
                    loading={isLoading}
                    paginationMode="server"
                    rowCount={questionMappingData?.data?.pagination?.total_count || 0}
                    pageSize={pageSize}
                    currentPage={page}
                    onPageChange={setPage}
                    totalPage={questionMappingData?.data?.pagination?.total_pages || 1}
                    rowHeight={65}
                />
            </Paper>
            <ResponseDialog response={alertResponse} />
        </Box >
    );
};

export default PredefinedMappingLayout;
